import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { ArrowRight } from "lucide-react";
import { PageTitle, Panel, StatCard, Btn, EmptyState } from "../components/ui";
import { useI18n } from "../i18n";
import { CL } from "../api";

const fmt = (n) => (n >= 1000 ? (n / 1000).toFixed(2) : n.toFixed(1));

export default function Overview() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [s, setS] = useState(null);

  useEffect(() => { CL.summary().then(setS).catch(() => {}); }, []);

  const totals = s?.totals || { scope1: 0, scope2: 0, scope3: 0 };
  const chartData = [
    { name: t("scope1"), value: totals.scope1, color: "#14512f" },
    { name: t("scope2"), value: totals.scope2, color: "#2f8f5b" },
    { name: t("scope3"), value: totals.scope3, color: "#e08a1e" },
  ];
  const hasData = (s?.counts?.activities || 0) > 0;

  return (
    <div data-testid="overview-page">
      <PageTitle
        title={t("dashboard")}
        subtitle={t("byScope")}
        action={<Btn onClick={() => nav("/scope-1")} data-testid="overview-add-activity">{t("addActivity")}<ArrowRight className="h-4 w-4" /></Btn>}
      />

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
        <StatCard testId="stat-total" label={t("totalEmissions")} value={`${fmt((s?.total || 0) / 1000)}`} sub={t("tCO2e")} />
        <StatCard testId="stat-scope1" label={t("scope1")} value={fmt((totals.scope1 || 0) / 1000)} sub={t("tCO2e")} accent="#14512f" />
        <StatCard testId="stat-scope2" label={t("scope2")} value={fmt((totals.scope2 || 0) / 1000)} sub={t("tCO2e")} accent="#2f8f5b" />
        <StatCard testId="stat-scope3" label={t("scope3")} value={fmt((totals.scope3 || 0) / 1000)} sub={t("tCO2e")} accent="#e08a1e" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel className="lg:col-span-2" data-testid="scope-chart">
          <h3 className="mb-6 font-display text-lg text-[#16321f]">{t("byScope")}</h3>
          {hasData ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#6b7268", fontSize: 12 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: "#8a8f83", fontSize: 11 }} />
                  <Tooltip formatter={(v) => `${v.toLocaleString()} ${t("kgco2e")}`} cursor={{ fill: "#f0f2ea" }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={90}>
                    {chartData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-72"><EmptyState text={t("noData")} /></div>
          )}
        </Panel>

        <Panel data-testid="top-categories">
          <h3 className="mb-6 font-display text-lg text-[#16321f]">{t("topCategories")}</h3>
          {s?.top_categories?.length ? (
            <ul className="space-y-4">
              {s.top_categories.map((c, i) => (
                <li key={i}>
                  <div className="mb-1 flex justify-between text-sm">
                    <span className="text-[#3a423b]">{c.category}</span>
                    <span className="font-medium text-[#16321f]">{c.emissions.toLocaleString()} {t("kgco2e")}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-[#eef1e9]">
                    <div className="h-full rounded-full bg-[#14512f]" style={{ width: `${Math.min(100, (c.emissions / (s.top_categories[0].emissions || 1)) * 100)}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState text={t("noData")} />
          )}
        </Panel>
      </div>
    </div>
  );
}
